import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder} from '@angular/forms';
import {Client} from '../client-form/client.model';
import {ClientService} from '../client-form/client.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {Project} from '../../pages/product/product.model';
import {BillService} from './bill.service';
import {InstrumentService} from '../instrument/instrument.service';
import {InstrumentType} from '../../interfaces/interfaces';
import {BillLine} from './bill.model';
import {MatSnackBar} from '@angular/material/snack-bar';
import {retry} from 'rxjs/operators';
import {DraftService} from '../../services/draft.service';

@Component({
    selector: 'bill-dialog',
    templateUrl: './bill.component.html',
    styleUrls: ['./bill.component.scss']
})
export class BillComponent implements OnInit{

    // products: Project[];
    private _client: BehaviorSubject<Client | null> = new BehaviorSubject<Client | null>(null);
    private _billLines: BehaviorSubject<BillLine[]> = new BehaviorSubject<BillLine[]>(null);
    edit = false;
    public sendTo: string | null;
    public orderNumber: string;
    public discount: number = 0;
    public discountType: 'percentage' | 'amount' = 'percentage';
    private _draftId: string | null = null;


    constructor(
        public matDialogRef: MatDialogRef<BillComponent>,
        @Inject(MAT_DIALOG_DATA) private _data: any,
        private _formBuilder: FormBuilder,
        private _clientService: ClientService,
        private _billService: BillService,
        private _instrumentService: InstrumentService,
        private _snackBar: MatSnackBar,
        private _draftService: DraftService

    )
    {
        // this.products = _data.products;
        if (_data.isDraft) {
            this._draftId = _data.id;
            this._client.next(_data.client);
            this.sendTo = _data.sendTo;
            this.orderNumber = _data.orderNumber;
            this.discount = _data.discount || 0;
            this.discountType = _data.discountType || 'percentage';
            this._billLines.next(_data.lines);
        } else {
            this._clientService.getClient(_data.client.id).subscribe(
                resp => {
                    this._client.next(resp);

                    this.sendTo = resp?.email || '';
                    this.orderNumber = '';
                }
            );
            const ids = _data.projects.map(project => project.id);
            this._billService.getProducts(ids).subscribe( resp => {
                if (!resp) {
                    return;
                }
                const lines: BillLine[]  = [];
                resp.forEach(project => {
                    lines.push({
                        projectId: project.id,
                        price: this.getPrice(project),
                        quantity: 1,
                        rate: this.getPrice(project),
                        line: this.getLine(project),
                        description: this.getDescription(project),
                        serviceDescription: project.description
                    });
                });
                this._billLines.next(lines);
            });
        }

    }

    ngOnInit(): void {
        this.matDialogRef.backdropClick().subscribe(() => {
            this.saveDraft();
        });

        this.matDialogRef.keydownEvents().subscribe(event => {
            if (event.key === 'Escape') {
                this.saveDraft();
            }
        });
    }

    get client$(): Observable<Client | null> {
        return this._client.asObservable();
    }

    get products$(): Observable<BillLine[]> {
        return this._billLines.asObservable();
    }


    billProjects(): void {
        this.saveDraft();
        const bill = {
            client: this._client.value,
            date: new Date(),
            lines: this._billLines.value,
            orderNumber: this.orderNumber,
            sendTo: this.sendTo,
            discount: this.discount,
            discountType: this.discountType,
            total: this.getTotal()
        };
        this._billService.createBilling(bill).subscribe(
            () => {
                if (this._draftId) {
                    this._draftService.deleteDraft(this._draftId);
                }
                this.matDialogRef.close('OK');
                this._snackBar.open('El borrador de la factura se creó en Odoo. Apruébelo allí para finalizar la facturación.', 'Ok', {
                    verticalPosition: 'top',
                    duration        : 6000
                });
            },
            error => {
                // Extraer el mensaje de error del servidor
                let errorMessage = 'Hubo un error al generar la factura';
                
                if (error.error && typeof error.error === 'string') {
                    // Si el mensaje viene con el prefijo ##, lo removemos
                    if (error.error.startsWith('##')) {
                        errorMessage = error.error.substring(2);
                    } else {
                        errorMessage = error.error;
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.matDialogRef.close('Error');
                this._snackBar.open(errorMessage, 'Ok', {
                    verticalPosition: 'top',
                    duration        : 4000
                });
            }
        );
    }

    noBillProjects(): void {
        this.saveDraft();
        const bill = {
            client: this._client.value,
            date: new Date(),
            lines: this._billLines.value,
            orderNumber: this.orderNumber,
            sendTo: null,
            discount: this.discount,
            discountType: this.discountType,
            total: this.getTotal()
        };
        this._billService.noBilling(bill).subscribe(
            () => {
                if (this._draftId) {
                    this._draftService.deleteDraft(this._draftId);
                }
                this.matDialogRef.close('OK');
                this._snackBar.open('La no factura se creo correctamente', 'Ok', {
                    verticalPosition: 'top',
                    duration        : 4000
                });
            },
            error => {
                // Extraer el mensaje de error del servidor
                let errorMessage = 'Hubo un error al generar la NF';

                if (error.error && typeof error.error === 'string') {
                    // Si el mensaje viene con el prefijo ##, lo removemos
                    if (error.error.startsWith('##')) {
                        errorMessage = error.error.substring(2);
                    } else {
                        errorMessage = error.error;
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.matDialogRef.close('Error');
                this._snackBar.open(errorMessage, 'Ok', {
                    verticalPosition: 'top',
                    duration        : 4000
                });
            }
        );
    }

    cancel(): void {
        if (this._draftId) {
            this._draftService.deleteDraft(this._draftId);
        }
        this.matDialogRef.close('Cancel');
    }

    getLine(product: Project): string {
        return product.projectNumber + ' - ' + product.name;
    }

    getDescription(product: Project): string {
        let result: string = ' ( ' + product.instrument.model.brand?.name + ' - ' + product.instrument.model.name + ' - ';
        result += ( product.instrument.clientCode ? product.instrument.clientCode  : product.instrument.serialNumber) + ' )';
        return result;
    }



    getPrice(product: Project): number {
        if (product.instrument?.model?.family) {
            const theFamily: InstrumentType = this._instrumentService.getInstrumentType(product.instrument.model.family.id);
            return theFamily && theFamily.calibrationPrice ? theFamily.calibrationPrice : null;
        } else {
            return null;
        }
    }

    formatPrice(price: number): string {
        return price ? new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }).format(price) : null;
    }

    getLinesTotal(): number {
        return this._billLines.value ? this._billLines.value.reduce((acc, line) => acc + line.price, 0) : 0;
    }

    getDiscountAmount(): number {
        if (this.discountType === 'percentage') {
            return (this.getLinesTotal() * (this.discount || 0)) / 100;
        } else {
            return this.discount || 0;
        }
    }

    getSubtotal(): number {
        return this.getLinesTotal() - this.getDiscountAmount();
    }

    getTotal(): number {
        return this.getSubtotal();
    }


    updateKeyWords($event: FocusEvent, billLine: BillLine): void {
        // billLine.rate = Number.parseFloat($event.returnValue);
        console.log($event);
    }

    updateRate(event: Event, billLine: BillLine): void {
        const inputValue = (event.target as HTMLInputElement).value;

        // Validar si el valor es un número
        const parsedValue = parseFloat(inputValue);
        if (!isNaN(parsedValue)) {
            billLine.rate = parsedValue; // Actualizar el valor en tiempo real
            billLine.price = billLine.rate * billLine.quantity;
            console.log('Rate actualizado:', billLine.rate);
            this.saveDraft();
        }
    }

    updateConcept(event: Event, billLine: BillLine): void {
        const inputValue = (event.target as HTMLInputElement).value;
        billLine.line = inputValue;
        this.saveDraft();
    }

    updateDescription(event: Event, billLine: BillLine): void {
        const inputValue = (event.target as HTMLInputElement).value;
        billLine.description = inputValue;
        this.saveDraft();
    }

    addNewLine(): void {
        const currentLines = this._billLines.value || [];
        const newLine: BillLine = {
            projectId: null,
            price: 0,
            quantity: 1,
            rate: 0,
            line: '',
            description: ''
        };
        this._billLines.next([...currentLines, newLine]);
        this.saveDraft();
    }

    removeLine(index: number): void {
        const currentLines = this._billLines.value || [];
        const updatedLines = currentLines.filter((_, i) => i !== index);
        this._billLines.next(updatedLines);
        this.saveDraft();
    }

    saveDraft(): void {
        const draft = {
            id: this._draftId,
            client: this._client.value,
            lines: this._billLines.value,
            orderNumber: this.orderNumber,
            sendTo: this.sendTo,
            discount: this.discount,
            discountType: this.discountType,
            isDraft: true
        };
        this._draftService.saveDraft(draft).then(id => {
            this._draftId = id;
        });
    }
}
